import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  equipmentParametersByEquipmentID,
  getParameter,
  listParameters,
} from "graphql/queries";
import { updateEquipmentParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import EquipmentParamatersTable from "components/equipments/parameters/EquipmentParametersTable";
import EquipmentParameterCreateForm from "./EquipmentParameterCreateForm";
import EquipmentParameterUpdateForm from "./EquipmentParameterUpdateForm";

const EquipmentParameters = ({
  equipmentID,
  equipmentName,
  visible,
  onClose,
}) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "equipment:create-param",
    "equipment:update-param",
    "equipment:delete-param",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [equipmentParameters, setEquipmentParameters] = useState([]);
  const [equipmentParametersLoading, setEquipmentParametersLoading] =
    useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentParameterId: null,
    equipmentParameterName: null,
  });
  const [modalEquipmentParameter, setModalEquipmentParameter] = useState({
    visible: false,
    equipmentParameterId: null,
  });

  const columnsMapped = useMemo(
    () =>
      equipmentParameters.map((item) => {
        return {
          ...item,
          parameterName: item.parameterNameStr || <Loader />,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      equipmentParameterId: item.id,
                      equipmentParameterName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipmentParameters]
  );

  const onNext = () => {
    fetchEquipmentParameters(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchEquipmentParameters(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchEquipmentParameters(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalEquipmentParameter({
      visible: true,
      action,
      equipmentParameterId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalEquipmentParameter({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchEquipmentParameters();
  };

  async function fetchParameterName(id) {
    try {
      const parameterData = await API.graphql(
        graphqlOperation(getParameter, {
          id,
        })
      );
      const parameterItem = parameterData.data.getParameter;
      return parameterItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchParameterName", err);
      return "";
    }
  }

  const getParameterNames = (list) => {
    list?.forEach(async (item) => {
      const parameterNameStr = await fetchParameterName(item.parameterID);

      setEquipmentParameters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          parameterNameStr,
        };

        return updatedList;
      });
    });
  };

  async function fetchEquipmentParameters(token, isNext = true, keywords) {
    try {
      setEquipmentParametersLoading(true);
      const limitMapped = 10;
      let filterObjList = [];

      if (keywords) {
        const parametersData = await API.graphql(
          graphqlOperation(listParameters, {
            filter: {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              deletedAt: { attributeExists: false },
            },
          })
        );
        const parametersItems = parametersData.data.listParameters.items;

        filterObjList =
          parametersItems?.map((param) => ({
            parameterID: { eq: param.id },
          })) ?? [];
      }

      const equipmentParametersData = await API.graphql(
        graphqlOperation(equipmentParametersByEquipmentID, {
          equipmentID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    ...filterObjList,
                    {
                      parameterValue: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const equipmentParametersItems =
        equipmentParametersData.data.equipmentParametersByEquipmentID.items;
      setEquipmentParameters(equipmentParametersItems);
      getParameterNames(equipmentParametersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            equipmentParametersData.data.equipmentParametersByEquipmentID
              .nextToken,
        };
      });
      setEquipmentParametersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentParametersLoading(false);
    } finally {
      setEquipmentParametersLoading(false);
    }
  }

  async function equipmentParameterDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateEquipmentParameter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateEquipmentParameter.id;
      setEquipmentParameters(
        equipmentParameters.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        equipmentParameterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipmentParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalEquipmentParameter.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {equipmentName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addEquipmentParameter}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {equipmentParametersLoading ? (
                  <Loader />
                ) : (
                  <EquipmentParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.equipmentParameterName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    equipmentParameterId: null,
                  })
                }
                onConfirm={() =>
                  equipmentParameterDelete(modalDetails.equipmentParameterId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalEquipmentParameter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipmentParameter.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipmentParameter.action === "create" ? (
                <>
                  {strings.modalEquipmentParameter.addTitle} - {equipmentName}
                </>
              ) : (
                <>
                  {strings.modalEquipmentParameter.updateTitle} -{" "}
                  {equipmentName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipmentParameter.action === "create" ? (
                <EquipmentParameterCreateForm
                  equipmentID={equipmentID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <EquipmentParameterUpdateForm
                  equipmentID={equipmentID}
                  equipmentParameterId={
                    modalEquipmentParameter.equipmentParameterId
                  }
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Parámetros de Equipo",
    successDeleted: "Parámetro de Equipo eliminado con éxito",
    actions: {
      addEquipmentParameter: "Agregar Parámetro de Equipo",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar parámetro de equipos",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el parámetro de equipo {name}?",
    },
    modalEquipmentParameter: {
      addTitle: "Agregar Parámetro de Equipo",
      updateTitle: "Actualizar Parámetro de Equipo",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default EquipmentParameters;
